import { audioBufferToWav } from "./utils";

// ─── Worker singleton ────────────────────────────────────────────────────────

let worker = null;
// Set to false if Worker construction fails so we stop retrying.
let workerSupported = typeof Worker !== "undefined";
let jobIdCounter = 0;

/** Map of pending encode jobs: jobId → { resolve, reject, onProgress } */
const pendingJobs = new Map();

function getWorker() {
  if (!workerSupported) return null;
  if (worker) return worker;

  try {
    const w = new Worker(new URL("../workers/mp3Encoder.worker.js", import.meta.url));

    w.onmessage = (e) => {
      const { id, type, value, dataUrl, error } = e.data;
      const job = pendingJobs.get(id);
      if (!job) return;

      if (type === "progress") {
        job.onProgress?.(value);
      } else if (type === "done") {
        pendingJobs.delete(id);
        job.resolve(dataUrl);
      } else if (type === "error") {
        pendingJobs.delete(id);
        job.reject(new Error(error));
      }
    };

    w.onerror = (e) => {
      console.warn("[audioEncoding] Worker error, disabling worker:", e.message);
      // Reject all pending jobs so callers can fall back
      pendingJobs.forEach((job) => job.reject(new Error("Worker error: " + e.message)));
      pendingJobs.clear();
      worker = null;
      workerSupported = false;
    };

    worker = w;
    return worker;
  } catch (err) {
    console.warn("[audioEncoding] Cannot create Worker, falling back to main thread:", err.message);
    workerSupported = false;
    return null;
  }
}

// ─── Main-thread fallback ────────────────────────────────────────────────────

/**
 * Encode on the main thread when Workers are unavailable.
 * Delegates to the legacy audioBufferToWav implementation: original sample rate,
 * original channel count, 128 kbps. Blocks the UI — only used as a last resort.
 * @param {AudioBuffer} audioBuffer
 * @returns {Promise<string>} MP3 base64 data URL
 */
async function encodeMainThread(audioBuffer) {
  return audioBufferToWav(audioBuffer);
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Encode an AudioBuffer to an MP3 base64 data URL.
 *
 * When a Web Worker is available (all modern browsers, Chrome extension, iframes
 * without restrictive sandbox), encoding runs off the main thread so the UI stays
 * responsive. A progress callback is fired with values 0.0 → 1.0 as encoding
 * proceeds so callers can drive a toast progress bar.
 *
 * If Workers are unavailable (unsafe sandbox, very old browser), the function falls
 * back to the legacy main-thread encoder (audioBufferToWav behaviour): original
 * sample rate, original channel count, 128 kbps CBR. Blocks the UI.
 *
 * @param {AudioBuffer} audioBuffer - Decoded audio from AudioContext.decodeAudioData
 * @param {(progress: number) => void} [onProgress] - Optional 0.0–1.0 progress callback
 * @returns {Promise<string>} MP3 as a base64 data URL ("data:audio/mpeg;base64,…")
 */
export async function encodeAudioBufferToMp3(audioBuffer, onProgress) {
  const w = getWorker();

  if (w) {
    console.log("[audioEncoding] Encoding via Web Worker");
    return new Promise((resolve, reject) => {
      const id = ++jobIdCounter;
      pendingJobs.set(id, { resolve, reject, onProgress });

      // Extract channel data into transferable ArrayBuffers.
      // We must .slice() each Float32Array because AudioBuffer channels cannot be detached.
      const channelData = [];
      const transferList = [];
      for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
        const copy = audioBuffer.getChannelData(c).slice();
        channelData.push(copy.buffer);
        transferList.push(copy.buffer);
      }

      w.postMessage(
        {
          id,
          channelData,
          numberOfChannels: audioBuffer.numberOfChannels,
          sampleRate: audioBuffer.sampleRate,
          durationSecs: audioBuffer.duration,
        },
        transferList
      );
    });
  }

  // Worker unavailable — fall back to main thread
  console.warn("[audioEncoding] Encoding on main thread (Worker unavailable)");
  return encodeMainThread(audioBuffer, onProgress);
}

// ─── Streaming encoder (bounded memory) ──────────────────────────────────────

/**
 * Streaming MP3 encoder for assembling a long segmented recording without
 * holding the whole thing in memory. Feed decoded AudioBuffers one at a time
 * with addBuffer(); only ONE chunk is ever in flight because addBuffer awaits
 * the worker's per-chunk ack (backpressure). finish() returns the MP3 bytes and
 * terminates the dedicated worker.
 *
 * Caller contract: serialize calls — await each addBuffer before the next, and
 * await finish() last.
 *
 * @param {{durationSecsEstimate?: number, onProgress?: (chunksDone:number)=>void}} opts
 * @returns {{ addBuffer(audioBuffer: AudioBuffer): Promise<void>, finish(): Promise<Uint8Array>, dispose(): void }}
 */
export function createStreamingMp3Encoder({ durationSecsEstimate = 0, onProgress } = {}) {
  const w = new Worker(new URL("../workers/mp3StreamEncoder.worker.js", import.meta.url));
  let acked = 0;
  let pending = null; // at most one in flight by the serial-use contract
  let dead = false; // once the worker errors or finishes, further use rejects (never posts to a dead worker)

  const terminate = () => {
    if (!dead) {
      dead = true;
      w.terminate();
    }
  };

  w.onmessage = (e) => {
    const { type } = e.data;
    const p = pending;
    if (type === "error") {
      pending = null;
      terminate(); // an errored worker can't be reused — free it now, don't leak it
      if (p) p.reject(new Error(e.data.error));
      return;
    }
    if (!p) return;
    if (type === "ready" && p.kind === "init") {
      pending = null;
      p.resolve();
    } else if (type === "chunkDone" && p.kind === "chunk") {
      pending = null;
      acked += 1;
      if (onProgress) onProgress(acked);
      p.resolve();
    } else if (type === "done" && p.kind === "finish") {
      pending = null;
      p.resolve(new Uint8Array(e.data.bytes));
    }
  };
  w.onerror = (e) => {
    const p = pending;
    pending = null;
    terminate();
    if (p) p.reject(new Error("Streaming encoder worker error: " + (e.message || "")));
  };

  const send = (message, transfer, kind) =>
    new Promise((resolve, reject) => {
      if (dead) {
        // The worker already errored or was disposed — fail fast instead of
        // posting to a dead worker and hanging on an ack that never comes.
        reject(new Error("Streaming encoder is no longer available"));
        return;
      }
      pending = { resolve, reject, kind };
      w.postMessage(message, transfer || []);
    });

  const ready = send({ type: "init", durationSecsEstimate }, [], "init");
  // init's rejection is surfaced at the addBuffer/finish await sites and handled
  // by terminate-on-error; swallow the standalone unhandledrejection here.
  ready.catch(() => {});

  return {
    async addBuffer(audioBuffer) {
      await ready;
      // Copy each channel into a transferable ArrayBuffer; transferring detaches
      // it so the main thread releases this segment's PCM immediately.
      const channelData = [];
      const transfer = [];
      for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
        const copy = audioBuffer.getChannelData(c).slice();
        channelData.push(copy.buffer);
        transfer.push(copy.buffer);
      }
      await send(
        {
          type: "chunk",
          channelData,
          numberOfChannels: audioBuffer.numberOfChannels,
          sampleRate: audioBuffer.sampleRate,
        },
        transfer,
        "chunk"
      );
    },
    async finish() {
      try {
        await ready;
        return await send({ type: "finalize" }, [], "finish");
      } finally {
        terminate();
      }
    },
    // Idempotent teardown so a caller can guarantee no worker leak in a finally,
    // even when the stream is abandoned mid-way (decode abort, error).
    dispose() {
      terminate();
    },
  };
}
